import {
  fetchEventSource,
  type EventSourceMessage
} from '@microsoft/fetch-event-source';
import { AuthService } from '../AuthService';
import { getJwtExp } from '@/utils/getJwtExp';
import {
  SOCKET_MAX_RECONNECT_ATTEMPTS,
  SOCKET_RECONNECT_MAX_DELAY_MS,
  SOCKET_RECONNECT_MIN_DELAY_MS,
  TOKEN_REFRESH_LEEWAY_MS
} from '@/constants/socketTimings';
import {
  readLastEventId,
  saveLastEventId
} from './lastEventIdStorage';

export interface IMercureClientConfig {
  topics: readonly string[];
  onMessage: (message: EventSourceMessage) => void;
}

class MercureAuthError extends Error {
  constructor() {
    super('Mercure hub отклонил авторизацию');
    this.name = 'MercureAuthError';
  }
}

/**
 * Низкоуровневый клиент Mercure (SSE) поверх @microsoft/fetch-event-source.
 *
 * Отвечает за жизненный цикл подключения: подписку на переданные топики с
 * актуальным `Authorization: Bearer`, проактивную ротацию токена по `exp`,
 * страховочный реконнект по 401 от хаба, переподключение с backoff и
 * сопровождение `Last-Event-ID`. О структуре событий ничего не знает —
 * отдаёт сырые сообщения через колбэк `onMessage`.
 */
export class MercureClient {
  private readonly topics: readonly string[];
  private readonly onMessage: (message: EventSourceMessage) => void;

  private abortController: AbortController | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private generation = 0;
  private isStopped = true;
  private isReconnecting = false;
  private hasReceivedData = false;

  constructor(config: IMercureClientConfig) {
    this.topics = config.topics;
    this.onMessage = config.onMessage;
  }

  start(): void {
    if (!this.isStopped) {
      return;
    }

    this.isStopped = false;
    this.openConnection();
  }

  stop(): void {
    this.isStopped = true;
    this.isReconnecting = false;
    this.reconnectAttempt = 0;
    this.generation += 1;
    this.clearTimers();
    this.abortController?.abort();
    this.abortController = null;
  }

  private clearTimers(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private buildUrl(): string | null {
    const rawUrl = import.meta.env.VITE_MERCURE_URL;

    if (typeof rawUrl !== 'string' || rawUrl.trim() === '') {
      return null;
    }

    try {
      const url = new URL(rawUrl, window.location.origin);
      this.topics.forEach((topic) => {
        url.searchParams.append('topic', topic);
      });
      return url.toString();
    } catch {
      return null;
    }
  }

  private scheduleExpiryRefresh(token: string): void {
    const expMs = getJwtExp(token);

    if (expMs === null) {
      return;
    }

    const fireIn = expMs - Date.now() - TOKEN_REFRESH_LEEWAY_MS;
    const safeDelay = Math.max(fireIn, 0);

    this.expiryTimer = setTimeout(() => {
      void this.reconnectWithFreshToken();
    }, safeDelay);
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempt >= SOCKET_MAX_RECONNECT_ATTEMPTS) {
      this.stop();
      return;
    }

    const delay = Math.min(
      SOCKET_RECONNECT_MIN_DELAY_MS * 2 ** this.reconnectAttempt,
      SOCKET_RECONNECT_MAX_DELAY_MS
    );
    this.reconnectAttempt += 1;

    this.reconnectTimer = setTimeout(() => {
      this.openConnection();
    }, delay);
  }

  private async reconnectWithFreshToken(): Promise<void> {
    if (this.isStopped || this.isReconnecting) {
      return;
    }

    this.isReconnecting = true;
    this.generation += 1;
    this.clearTimers();
    this.abortController?.abort();
    this.abortController = null;

    try {
      await AuthService.refreshAuthToken();
    } catch {
      // refreshAuthToken сам разлогинивает при фатальной ошибке;
      // при временной — пробуем переподключиться с backoff.
    } finally {
      this.isReconnecting = false;
    }

    if (this.isStopped) {
      return;
    }

    this.reconnectAttempt = 0;
    this.openConnection();
  }

  private openConnection(): void {
    if (this.isStopped) {
      return;
    }

    const token = AuthService.authToken;

    if (token === null) {
      return;
    }

    const url = this.buildUrl();

    if (url === null) {
      return;
    }

    this.generation += 1;
    const connectionGeneration = this.generation;
    const controller = new AbortController();
    this.abortController = controller;

    this.scheduleExpiryRefresh(token);

    const lastEventId = readLastEventId();
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`
    };
    if (lastEventId !== null) {
      headers['Last-Event-ID'] = lastEventId;
    }

    void fetchEventSource(url, {
      headers,
      signal: controller.signal,
      openWhenHidden: true,
      onopen: async (response) => {
        if (response.status === 401 || response.status === 403) {
          throw new MercureAuthError();
        }
        // Счётчик попыток сбрасываем не по факту HTTP 200, а по факту живого
        // стрима (первый кадр в onmessage): «ложный 200» от заглушки/SPA-фолбэка
        // тут же закрывается и не должен обнулять backoff.
        if (this.generation === connectionGeneration) {
          this.hasReceivedData = false;
        }
      },
      onmessage: (message) => {
        if (this.generation !== connectionGeneration) {
          return;
        }

        // Первый реальный кадр (keep-alive или сообщение) = стрим живой:
        // только теперь сбрасываем backoff.
        if (!this.hasReceivedData) {
          this.hasReceivedData = true;
          this.reconnectAttempt = 0;
        }

        if (message.id !== '') {
          saveLastEventId(message.id);
        }

        // Keep-alive Mercure приходит как сообщение с пустым data.
        if (message.data === '') {
          return;
        }

        this.onMessage(message);
      },
      onclose: () => {
        // Бросаем, чтобы библиотека не делала свой авто-реконнект:
        // переподключением управляем сами через onerror/scheduleReconnect.
        throw new Error('Mercure connection closed');
      },
      onerror: (error) => {
        if (this.generation !== connectionGeneration || this.isStopped) {
          throw error;
        }

        if (error instanceof MercureAuthError) {
          void this.reconnectWithFreshToken();
          throw error;
        }

        this.scheduleReconnect();
        throw error;
      }
    });
  }
}
